import React, { useEffect, useState, useRef } from "react";
import "./Nav.scss";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { NavDropdown, Button } from "react-bootstrap";
import logoIUH from "../../resource/img/Logo_IUH.png";
import { logoutUser } from "../../services/userServices";
import { toast } from "react-toastify";
import { logout } from "../../redux/slice/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";

const NavHeader = () => {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const userCode = user?.userData?.userCode || user?.userData?.userData?.userCode || user?.account?.userCode || "";

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!userCode) return;

    const socket = io(process.env.REACT_APP_NOTIFICATION_URL || window.location.origin, {
      transports: ["websocket"],
      autoConnect: true,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("register", userCode);
    });

    socket.on("new_notification", (data) => {
      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });

    return () => {
      if (socket) {
        socket.off("connect");
        socket.off("new_notification");
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [userCode]);

  const handleLogout = async () => {
    let data = await logoutUser();
    dispatch(logout());
    if (data?.ok) {
      toast.success("Đã đăng xuất");
      navigate("/login");
    } else {
      toast.error(data?.message || "Đã đăng xuất");
      navigate("/login");
    }
  };

  // Helper to get initial letter for avatar badge
  const getInitialLetter = () => {
    const name = user?.userData?.fullName || user?.userData?.name || user?.userData?.userCode || user?.account?.userCode || "";
    if (!name) return "U";
    const parts = name.trim().split(/\s+/);
    if (parts.length > 0) {
      const lastWord = parts[parts.length - 1];
      return lastWord.charAt(0).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="smartpark-header-wrapper">
      {/* Top Info Bar */}
      <div className="top-info-bar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <i className="bi bi-building text-primary"></i>
          <span>Trường Đại học Công nghiệp TP.HCM - Bãi Đỗ Xe Thông Minh IUH Parking</span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center gap-1">
            <i className="bi bi-telephone-fill text-primary"></i>
            <span>0283.8940 390 - ext 838</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <i className="bi bi-envelope-fill text-primary"></i>
            <span>csm@iuh.edu.vn</span>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className="main-navbar-container">
        {/* Brand Logo */}
        <Link to="/" className="brand-logo-link">
          <img src={logoIUH} alt="Logo IUH" className="logo-iuh-img" />
          <div className="brand-text">
            <span>IUH Parking</span>
            <span className="brand-sub">ĐẠI HỌC CÔNG NGHIỆP TP.HCM</span>
          </div>
        </Link>

        {/* Header Links & Actions */}
        <div className="d-flex align-items-center gap-3">
          <div className="header-nav-menu">
            <NavLink to="/" className={({ isActive }) => `nav-link-custom ${isActive && location.pathname === '/' ? 'active' : ''}`}>
              <i className="bi bi-house me-1"></i>
              Trang chủ
            </NavLink>
            <NavLink to="/history" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
              <i className="bi bi-clock-history me-1"></i>
              Lịch sử ra vào
            </NavLink>
          </div>

          {/* User Actions */}
          <div className="header-user-actions ms-2">
            {/* Notification Bell */}
            <div className="position-relative">
              <button
                type="button"
                className="notification-bell-btn"
                onClick={() => setShowNotifications((prev) => !prev)}
                title="Thông báo"
              >
                <i className="bi bi-bell fs-5"></i>
                {unreadCount > 0 && (
                  <span className="badge-count">{unreadCount}</span>
                )}
              </button>

              {/* Notification Popover */}
              {showNotifications && (
                <div
                  className="position-absolute shadow-lg rounded-3 bg-white"
                  style={{
                    top: "120%",
                    right: 0,
                    width: "320px",
                    zIndex: 1050,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center p-3 border-bottom bg-light">
                    <strong className="text-dark">Thông báo</strong>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-decoration-none"
                      onClick={() => setUnreadCount(0)}
                    >
                      Đánh dấu đã đọc
                    </Button>
                  </div>
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {notifications.length === 0 ? (
                      <p className="text-center text-muted my-4 small">Không có thông báo mới</p>
                    ) : (
                      notifications.map((noti, index) => (
                        <div key={index} className="p-3 border-bottom hover-bg">
                          <strong className="d-block text-dark small">{noti.title || "Thông báo hệ thống"}</strong>
                          <p className="mb-1 text-muted small">{noti.message || noti.content}</p>
                          <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {new Date(noti.timestamp || Date.now()).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Auth Button or User Menu */}
            {user?.isAuthenticated ? (
              <NavDropdown
                title={
                  <div className="d-flex align-items-center gap-1">
                    {user?.userData?.avatar && !user.userData.avatar.includes("pravatar") ? (
                      <img
                        src={user.userData.avatar}
                        alt="Avatar"
                        className="rounded-circle border"
                        style={{ width: "36px", height: "36px", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="user-avatar-badge">
                        <span>{getInitialLetter()}</span>
                      </div>
                    )}
                    <i className="bi bi-chevron-down text-muted small ms-1"></i>
                  </div>
                }
                id="user-profile-dropdown"
                align="end"
              >
                <NavDropdown.Item onClick={handleLogout} className="text-danger">
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Đăng xuất
                </NavDropdown.Item>
              </NavDropdown>
            ) : (
              <Link to="/login" className="btn-login-nav">
                Đăng nhập
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NavHeader;
